/**
 * Trupanion Results Display
 * Renders the insurance summary in Trupanion's report format.
 *
 * Sections:
 * 1. Pet Details (breed, effective date, age)
 * 2. Records provided by (hospitals)
 * 3. "What we won't cover" (pre-existing conditions table)
 * 4. "Related illness or injury" (predisposing conditions)
 * 5. "Things to note" (symptoms/signs table)
 * 6. "Additional medical history" (treatments/vaccinations, collapsible)
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Link,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';
import { useFlashMessagesContext } from '../../contexts/FlashMessagesContext';
import DiseaseModal from '../DiseaseModal';
import type {
  GetReportResponseCompleted,
  PreExistingCondition,
  ThingToNote,
  AdditionalMedicalHistoryItem,
} from '../../types/insurance';

interface TrupanionResultsDisplayProps {
  results: GetReportResponseCompleted;
  onReset: () => void;
}

const INSURANCE_PROMPT_EXAMPLES = [
  'Assess this pet\'s insurability based on the medical history',
  'Which pre-existing conditions would likely affect coverage?',
  'Summarize the risk profile for underwriting',
  'Are there any breed-related predispositions to flag?',
];

/**
 * Group findings by body system, preserving order.
 * Returns array of [bodySystem, items[]] tuples.
 */
function groupByBodySystem<T extends { bodySystem: string }>(items: T[]): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    if (!groups.has(item.bodySystem)) {
      groups.set(item.bodySystem, []);
    }
    groups.get(item.bodySystem)!.push(item);
  }
  return Array.from(groups.entries());
}

/**
 * Render a Trupanion-style findings table with body system grouping.
 * Body system is shown once per group (rowSpan).
 */
function FindingsTable({ findings, noteField, onDiseaseClick }: {
  findings: Array<PreExistingCondition | ThingToNote>;
  noteField: 'veterinarianNotes';
  onDiseaseClick?: (diseaseId: number, diseaseName: string) => void;
}) {
  const groups = groupByBodySystem(findings);

  return (
    <TableContainer>
      <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Body system</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '38%' }}>Veterinarian notes</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Date of visit</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Hospital</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(([bodySystem, items]) =>
            items.map((item, idx) => (
              <TableRow key={`${bodySystem}-${idx}`}>
                {idx === 0 ? (
                  <TableCell
                    rowSpan={items.length}
                    sx={{ verticalAlign: 'top', fontWeight: 500 }}
                  >
                    {bodySystem}
                  </TableCell>
                ) : null}
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {(item as any).diseaseId && onDiseaseClick ? (
                    <Link
                      component="button"
                      underline="hover"
                      color="primary"
                      sx={{ textAlign: 'left', font: 'inherit' }}
                      onClick={() => onDiseaseClick((item as any).diseaseId, (item as any).diseaseName || item[noteField])}
                    >
                      {item[noteField]}
                    </Link>
                  ) : (
                    item[noteField]
                  )}
                </TableCell>
                <TableCell>{item.dateOfVisit}</TableCell>
                <TableCell>{item.hospital}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TrupanionResultsDisplay: React.FC<TrupanionResultsDisplayProps> = ({ results, onReset }) => {
  const { addMessage } = useFlashMessagesContext();
  const navigate = useNavigate();
  const [chatInput, setChatInput] = useState('');
  const [diseaseModalOpen, setDiseaseModalOpen] = useState(false);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<number>(0);
  const [selectedDiseaseName, setSelectedDiseaseName] = useState<string>('');

  const handleDiseaseClick = (diseaseId: number, diseaseName: string) => {
    setSelectedDiseaseId(diseaseId);
    setSelectedDiseaseName(diseaseName);
    setDiseaseModalOpen(true);
  };

  const petName = results.petDetails.name;

  // Calculate age at effective date from DOB if available
  const ageAtEffective = (() => {
    if (results.petDetails.age) return results.petDetails.age;
    if (!results.petDetails.dateOfBirth) return null;
    try {
      const dob = new Date(results.petDetails.dateOfBirth);
      const eff = new Date(results.petDetails.effectiveDate);
      let years = eff.getFullYear() - dob.getFullYear();
      const monthDiff = eff.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && eff.getDate() < dob.getDate())) {
        years--;
      }
      const months = (eff.getMonth() - dob.getMonth() + 12) % 12;
      if (years === 0) return `${months} months old`;
      if (months === 0) return `${years} year${years !== 1 ? 's' : ''} old`;
      return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''} old`;
    } catch {
      return null;
    }
  })();

  const handleStartChat = (message: string) => {
    if (!message.trim()) return;
    const newConversationId = crypto.randomUUID();
    navigate(`/c/${newConversationId}`, {
      state: {
        insuranceReportId: results.reportId,
        initialMessage: message.trim(),
      },
    });
  };

  const formatForClipboard = (): string => {
    const lines: string[] = [];

    // Header
    lines.push(`${petName}'s Medical Record Summary`);
    lines.push('='.repeat(60));
    lines.push('');

    // Pet details
    lines.push('PET DETAILS');
    lines.push(`Breed: ${results.petDetails.breed}`);
    lines.push(`Effective Date: ${results.petDetails.effectiveDate}`);
    if (results.petDetails.age) {
      lines.push(`Age at Effective Date: ${results.petDetails.age}`);
    }
    lines.push('');

    // Records provided by
    lines.push('RECORDS PROVIDED BY');
    lines.push('-'.repeat(60));
    results.hospitalsVisited.forEach((hospital) => {
      lines.push(hospital.name);
      if (hospital.address) {
        lines.push(hospital.address);
      }
      lines.push('');
    });

    // What we won't cover
    lines.push('WHAT WE WON\'T COVER');
    lines.push('-'.repeat(60));
    if (results.preExistingConditions.length === 0) {
      lines.push('None');
    } else {
      lines.push('Body System | Veterinarian Notes | Date of Visit | Hospital');
      lines.push('-'.repeat(60));
      results.preExistingConditions.forEach((c) => {
        lines.push(`${c.bodySystem} | ${c.veterinarianNotes} | ${c.dateOfVisit} | ${c.hospital}`);
      });
    }
    lines.push('');

    // Related illness or injury
    if (results.relatedIllnessOrInjury && results.relatedIllnessOrInjury.length > 0) {
      lines.push('RELATED ILLNESS OR INJURY');
      lines.push('-'.repeat(60));
      lines.push('Related illness or injury | How we\'ll look at it');
      lines.push('-'.repeat(60));
      results.relatedIllnessOrInjury.forEach((r) => {
        lines.push(`${r.condition} | ${r.description}`);
      });
      lines.push('');
    }

    // Things to note
    lines.push('THINGS TO NOTE');
    lines.push('-'.repeat(60));
    if (results.thingsToNote.length === 0) {
      lines.push('None');
    } else {
      lines.push('Body System | Veterinarian Notes | Date of Visit | Hospital');
      lines.push('-'.repeat(60));
      results.thingsToNote.forEach((t) => {
        lines.push(`${t.bodySystem} | ${t.veterinarianNotes} | ${t.dateOfVisit} | ${t.hospital}`);
      });
    }
    lines.push('');

    // Additional medical history
    if (results.additionalMedicalHistory && results.additionalMedicalHistory.length > 0) {
      lines.push('ADDITIONAL MEDICAL HISTORY');
      lines.push('-'.repeat(60));
      results.additionalMedicalHistory.forEach((item) => {
        lines.push(`${item.condition} (${item.bodySystem}) - ${item.dateOfVisit} - ${item.hospital}`);
      });
    }

    return lines.join('\n');
  };

  const handleCopyToClipboard = async () => {
    try {
      const text = formatForClipboard();
      await navigator.clipboard.writeText(text);
      addMessage({
        id: crypto.randomUUID(),
        type: 'success',
        title: 'Copied to Clipboard',
        message: 'Summary has been copied to clipboard',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      addMessage({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'Copy Failed',
        message: 'Failed to copy to clipboard',
      });
    }
  };

  const handleDownloadJSON = () => {
    try {
      const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insurance-summary-trupanion-${results.reportId}.json`;
      link.click();
      URL.revokeObjectURL(url);

      addMessage({
        id: crypto.randomUUID(),
        type: 'success',
        title: 'Downloaded',
        message: 'Summary has been downloaded as JSON',
      });
    } catch (error) {
      console.error('Failed to download:', error);
      addMessage({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to download JSON file',
      });
    }
  };

  const additionalHistory = results.additionalMedicalHistory || [];
  const relatedIllnesses = results.relatedIllnessOrInjury || [];

  // Group additional history by subtype for display
  const historyBySubtype = new Map<string, AdditionalMedicalHistoryItem[]>();
  for (const item of additionalHistory) {
    const label = item.findingSubtype === 'vaccination' ? 'Vaccinations'
      : item.findingSubtype === 'preventive_care' ? 'Preventive Care'
      : item.findingSubtype === 'procedure' ? 'Procedures'
      : 'Treatments';
    if (!historyBySubtype.has(label)) {
      historyBySubtype.set(label, []);
    }
    historyBySubtype.get(label)!.push(item);
  }

  return (
    <Box>
      {/* Action buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyToClipboard}>
          Copy Full Summary
        </Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadJSON}>
          Download JSON
        </Button>
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onReset}>
          New Report
        </Button>
      </Stack>

      {/* Summary stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <Chip
          label={`${results.preExistingConditions.length} Pre-Existing`}
          color="error"
          variant="outlined"
        />
        <Chip
          label={`${results.thingsToNote.length} Things to Note`}
          color="warning"
          variant="outlined"
        />
        {additionalHistory.length > 0 && (
          <Chip
            label={`${additionalHistory.length} Additional History`}
            color="default"
            variant="outlined"
          />
        )}
        <Chip
          label="Trupanion Format"
          color="primary"
          size="small"
        />
      </Stack>

      {/* Pet Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
          {petName}&apos;s Medical Record Summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your coverage uncovered
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
          {petName}&apos;s details
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Breed:</TableCell>
                <TableCell sx={{ border: 'none' }}>{results.petDetails.breed}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: 'none' }}>Effective date:</TableCell>
                <TableCell sx={{ border: 'none' }}>{results.petDetails.effectiveDate}</TableCell>
                {ageAtEffective && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', border: 'none' }}>Age at effective date:</TableCell>
                    <TableCell sx={{ border: 'none' }}>{ageAtEffective}</TableCell>
                  </>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Records provided by */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
          Records provided by:
        </Typography>
        {results.hospitalsVisited.map((hospital, i) => (
          <Box key={i} sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {hospital.name}
            </Typography>
            {hospital.address && (
              <Typography variant="body2" color="text.secondary">
                {hospital.address}
              </Typography>
            )}
          </Box>
        ))}
      </Paper>

      {/* What we won't cover */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          What we won&apos;t cover
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The things listed below were present before {petName}&apos;s coverage started.
          These and anything related will not be covered.
        </Typography>

        {results.preExistingConditions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No pre-existing conditions found.
          </Typography>
        ) : (
          <FindingsTable
            findings={results.preExistingConditions}
            noteField="veterinarianNotes"
            onDiseaseClick={handleDiseaseClick}
          />
        )}
      </Paper>

      {/* Related illness or injury */}
      {relatedIllnesses.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The things listed below are specific to {petName} or are directly related to their
            health history and will not be covered.
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Related illness or injury</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>How we&apos;ll look at it</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatedIllnesses.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      {r.diseaseId ? (
                        <Link
                          component="button"
                          underline="hover"
                          color="primary"
                          sx={{ textAlign: 'left', font: 'inherit' }}
                          onClick={() => handleDiseaseClick(r.diseaseId!, r.condition)}
                        >
                          {r.condition}
                        </Link>
                      ) : (
                        r.condition
                      )}
                    </TableCell>
                    <TableCell>{r.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Things to note */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Things to note
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The things listed below can be signs or evidence of problems that were present before
          {' '}{petName}&apos;s coverage started. If {petName} has any costs related to these signs,
          our claims team will give your invoice additional review, and it may not be eligible for coverage.
        </Typography>

        {results.thingsToNote.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No findings to note.
          </Typography>
        ) : (
          <FindingsTable
            findings={results.thingsToNote}
            noteField="veterinarianNotes"
            onDiseaseClick={handleDiseaseClick}
          />
        )}
      </Paper>

      {/* Additional Medical History (collapsible) */}
      {additionalHistory.length > 0 && (
        <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Additional Medical History ({additionalHistory.length} items)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The following treatments, vaccinations, and procedures were found in the medical records
              but are not part of the standard Trupanion report format.
            </Typography>
            {Array.from(historyBySubtype.entries()).map(([label, items]) => (
              <Box key={label} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {label}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Body System</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Hospital</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.condition}</TableCell>
                          <TableCell>{item.bodySystem}</TableCell>
                          <TableCell>{item.dateOfVisit}</TableCell>
                          <TableCell>{item.hospital}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Ask about this report */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <ChatIcon color="primary" />
          <Typography variant="h6">Ask about this report</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {INSURANCE_PROMPT_EXAMPLES.map((prompt) => (
            <Chip
              key={prompt}
              label={prompt}
              onClick={() => handleStartChat(prompt)}
              clickable
              variant="outlined"
              color="primary"
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask a question about this insurance report..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleStartChat(chatInput);
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={() => handleStartChat(chatInput)}
            disabled={!chatInput.trim()}
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </Paper>

      {/* Processed Files */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Processed Files
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {results.processedFiles.map((file, index) => (
          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
            {file.success ? '\u2705' : '\u274C'} {file.filename}
            {file.success ? ` - ${file.findingsExtracted} findings extracted` : ` - ${file.error || 'Processing failed'}`}
          </Typography>
        ))}
      </Paper>

      {/* Metadata */}
      <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">
          Report ID: {results.reportId} | Created: {new Date(results.metadata.createdAt).toLocaleString()} |
          Processing Time: {(results.metadata.processingTimeMs / 1000).toFixed(1)}s | Format: Trupanion
        </Typography>
      </Paper>

      {/* Disease Detail Modal */}
      <DiseaseModal
        open={diseaseModalOpen}
        onClose={() => setDiseaseModalOpen(false)}
        diseaseId={selectedDiseaseId}
        diseaseName={selectedDiseaseName}
      />
    </Box>
  );
};

export default TrupanionResultsDisplay;
